import React, { useEffect, useRef, useState } from "react";
import {
  APIProvider,
  Map,
  Marker,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { GOOGLE_MAPS_API_KEY, MAP_ID } from "../../config";
import { useMapColorScheme } from "../../hooks/useMapColorScheme";

const DEFAULT_RADIUS_M = 150;
const RADIUS_MIN = 25;
const RADIUS_MAX = 1000;
const CLOSE_ZOOM = 16;
// Fallback map view (continental US) when we have no center and no address.
const FALLBACK_CENTER = { lat: 39.5, lng: -98.35 };

// Imperatively draws / updates a google.maps.Circle for the radius, since
// @vis.gl/react-google-maps doesn't ship a Circle component.
function RadiusCircle({ center, radius }) {
  const map = useMap();
  const mapsLib = useMapsLibrary("maps");
  const circleRef = useRef(null);

  useEffect(() => {
    if (!map || !mapsLib || !center) return;
    if (!circleRef.current) {
      circleRef.current = new mapsLib.Circle({
        map,
        strokeColor: "#2563eb",
        strokeOpacity: 0.8,
        strokeWeight: 1,
        fillColor: "#2563eb",
        fillOpacity: 0.15,
        clickable: false,
      });
    }
    circleRef.current.setCenter(center);
    circleRef.current.setRadius(radius);
  }, [map, mapsLib, center, radius]);

  // Remove the circle from the map on unmount.
  useEffect(
    () => () => {
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
    },
    []
  );

  return null;
}

// Pans/zooms the map to a target coordinate when it changes (e.g. a freshly
// chosen address). Guarded so dragging the pin afterwards doesn't snap the view
// back, and so it only fires once per distinct target.
function MapRecenter({ target }) {
  const map = useMap();
  const appliedRef = useRef(null);

  useEffect(() => {
    if (!map || !target) return;
    const key = `${target.lat},${target.lng}`;
    if (appliedRef.current === key) return;
    appliedRef.current = key;
    map.panTo(target);
    map.setZoom(CLOSE_ZOOM);
  }, [map, target]);

  return null;
}

// Keeps the whole radius circle visible: whenever the radius changes, fit the
// map to the circle's bounds (with padding). Computed from the actual circle
// bounds rather than a hand-mapped radius→zoom table, so it stays correct at any
// map size. Fires on radius change only (reads center from a ref) so dragging or
// panning the pin doesn't force a re-zoom.
function FitRadius({ center, radius }) {
  const map = useMap();
  const mapsLib = useMapsLibrary("maps");
  const centerRef = useRef(center);
  centerRef.current = center;
  const lastRadiusRef = useRef(null);

  useEffect(() => {
    if (!map || !mapsLib || !centerRef.current) return;
    if (lastRadiusRef.current === radius) return;
    lastRadiusRef.current = radius;
    const bounds = new mapsLib.Circle({
      center: centerRef.current,
      radius,
    }).getBounds();
    if (bounds) map.fitBounds(bounds, 30); // 30px padding on all sides
  }, [map, mapsLib, radius]);

  return null;
}

// The actual map, rendered inside an APIProvider. When the fence has no center
// yet but the site has a saved address, we geocode that address to seed a
// close-in center (rather than falling back to a zoomed-out view). The
// client-side geocoder is part of Maps JS — no separate Geocoding API required.
function GeofenceMap({ center, radius, address, seedCoords, onCenter }) {
  const colorScheme = useMapColorScheme();
  const geocodingLib = useMapsLibrary("geocoding");
  // Show a "locating" state (instead of flashing a world map) while we geocode.
  const [locating, setLocating] = useState(() => !center && !!address);
  const triedRef = useRef(false);

  // Re-arm geocoding if the address changes while we still have no center.
  useEffect(() => {
    triedRef.current = false;
    if (!center && address) setLocating(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  useEffect(() => {
    if (center || !address || !geocodingLib || triedRef.current) return;
    triedRef.current = true;
    const geocoder = new geocodingLib.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      setLocating(false);
      if (status === "OK" && results && results[0]) {
        const loc = results[0].geometry.location;
        onCenter(loc.lat(), loc.lng());
      }
    });
  }, [center, address, geocodingLib, onCenter]);

  if (!center && locating) {
    return (
      <div
        style={{ height: "220px" }}
        className="rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400"
      >
        Locating address…
      </div>
    );
  }

  return (
    <div className="rounded-xl overflow-hidden">
      <Map
        key={colorScheme}
        defaultCenter={center || FALLBACK_CENTER}
        defaultZoom={center ? CLOSE_ZOOM : 4}
        style={{ width: "100%", height: "220px" }}
        mapId={MAP_ID}
        colorScheme={colorScheme}
        options={{ disableDefaultUI: true, gestureHandling: "greedy" }}
        onClick={(e) => {
          const ll = e.detail?.latLng;
          if (ll) onCenter(ll.lat, ll.lng);
        }}
      >
        {center && (
          <Marker
            position={center}
            draggable
            onDragEnd={(e) => {
              const ll = e.latLng;
              if (ll) onCenter(ll.lat(), ll.lng());
            }}
          />
        )}
        {center && <RadiusCircle center={center} radius={radius} />}
        {center && <FitRadius center={center} radius={radius} />}
        <MapRecenter target={seedCoords} />
      </Map>
    </div>
  );
}

// Site "clock-in area" editor: a draggable pin (center) + a radius slider.
// The area is just data on the site — a SHIFT decides whether to enforce it
// — so there's no on/off here. It's always shown, auto-centered on the site's
// address (geocoded) with a sensible default radius, and adjustable.
// `value` is { latitude, longitude, radius_m }; `seedCoords` ({ lat, lng })
// seeds the center from a freshly picked address; `address` (formatted string)
// is geocoded to center the map for an existing site.
function GeofenceEditor({ value, onChange, seedCoords, address }) {
  const hasCenter = value?.latitude != null && value?.longitude != null;
  const center = hasCenter
    ? { lat: Number(value.latitude), lng: Number(value.longitude) }
    : null;
  const radius = value?.radius_m ? Number(value.radius_m) : DEFAULT_RADIUS_M;

  const setCenter = (lat, lng) =>
    onChange({ latitude: lat, longitude: lng, radius_m: radius });

  const setRadius = (r) =>
    onChange({
      latitude: value?.latitude ?? null,
      longitude: value?.longitude ?? null,
      radius_m: r,
    });

  // When the admin picks an address, move the pin to it — but only once per
  // distinct address, so a subsequent manual drag of the pin sticks.
  const lastSeedRef = useRef(null);
  useEffect(() => {
    if (!seedCoords) return;
    const key = `${seedCoords.lat},${seedCoords.lng}`;
    if (lastSeedRef.current === key) return;
    lastSeedRef.current = key;
    setCenter(seedCoords.lat, seedCoords.lng);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedCoords]);

  return (
    <div className="sm:col-span-3">
      <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-300">
        Clock-in geofence
      </label>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Where guards must be to clock in — applied only when a shift requires it.
        Set automatically from the address; drag the pin or change the radius to
        fine-tune.
      </p>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-300">Radius</span>
          <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
            {radius} m
          </span>
        </div>
        <input
          type="range"
          min={RADIUS_MIN}
          max={RADIUS_MAX}
          step={25}
          value={radius}
          onChange={(e) => setRadius(Number(e.target.value))}
          className="w-full"
        />

        <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
          <GeofenceMap
            center={center}
            radius={radius}
            address={address}
            seedCoords={seedCoords}
            onCenter={setCenter}
          />
        </APIProvider>

        <p className="text-xs text-gray-500 dark:text-gray-400">
          {center
            ? "Drag the pin or tap the map to move the center."
            : "Add an address above, or tap the map to place the center."}
        </p>
      </div>
    </div>
  );
}

export { GeofenceEditor };
